import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Skeleton for PackageDetail. Mirrors the real layout:
//   breadcrumb · forest-green hero (icon + title + campaign pill + price) ·
//   4-col metric tiles · 2-col body (left: About / Why / Preparation stack,
//   right: Tests Included list) · FAQ rows · bottom CTA band.
//
// TestDetail and PackageDetail look superficially similar but the spec
// cards are different: packages use centered 4-col metric tiles and a
// stacked prose body, not the 2×(key-value grid) used by tests.

// Centered metric tile. Icon dot · uppercase label · value.
const MetricTileShimmer = () => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid var(--border-l)',
      borderRadius: '14px',
      p: 2.25,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.75,
    }}
  >
    <Shimmer width={26} height={26} circular />
    <Shimmer width={70} height={9} rounded={4} sx={{ mt: 0.5 }} />
    <Shimmer width={90} height={12} rounded={4} />
  </Box>
);

// Card with dark section band + prose paragraph. Used for About / Why.
const ProseCardShimmer = ({ lines = 4, extraTags = false }) => (
  <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
    <Box sx={{ bgcolor: 'var(--forest)', p: '10px 18px' }}>
      <Shimmer width={140} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
    </Box>
    <Box sx={{ p: 2.5 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer
          key={i}
          width={i === lines - 1 ? '70%' : '100%'}
          height={11}
          rounded={4}
          sx={{ mb: i < lines - 1 ? 1 : 0 }}
        />
      ))}
      {extraTags && (
        <>
          <Shimmer width={70} height={10} rounded={4} sx={{ mt: 2, mb: 1 }} />
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Shimmer key={i} width={68 + i * 8} height={22} rounded={100} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  </Box>
);

const PackageDetailShimmer = () => (
  <Box sx={{ bgcolor: 'var(--cream)', minHeight: '100vh' }}>
    {/* Breadcrumb */}
    <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', py: 1.75 }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Shimmer width={90} height={11} rounded={4} />
          <Shimmer width={8} height={11} rounded={4} />
          <Shimmer width={160} height={11} rounded={4} />
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 4.5, px: { xs: 2, md: 4 } }}>
      {/* Hero — forest green */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          borderRadius: '20px',
          p: { xs: 3, md: 3.5 },
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Shimmer width={64} height={64} rounded="14px" sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Tag pill */}
            <Shimmer width={90} height={18} rounded={100} sx={{ bgcolor: 'rgba(255,255,255,0.12)', mb: 1.25 }} />
            {/* Title */}
            <Shimmer width="75%" height={32} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)', mb: 1 }} />
            {/* Meta row */}
            <Stack direction="row" spacing={1}>
              <Shimmer width={80} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
              <Shimmer width={90} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
              <Shimmer width={60} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
            </Stack>
          </Box>

          {/* Price + buttons */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              {/* Campaign pill */}
              <Shimmer width={110} height={14} rounded={100} sx={{ bgcolor: 'rgba(201,125,8,0.3)', mb: 0.75, ml: { xs: 0, md: 'auto' } }} />
              <Shimmer width={110} height={28} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />
            </Box>
            <Stack spacing={1} sx={{ flex: { xs: 1, md: 'initial' } }}>
              <Shimmer width={120} height={36} rounded={100} sx={{ bgcolor: 'rgba(201,125,8,0.45)' }} />
              <Shimmer width={120} height={30} rounded={100} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Metric tiles — 2-col on mobile, 4-col on desktop */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1.5, mb: 2, width: '100%',
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => <MetricTileShimmer key={i} />)}
      </Box>

      {/* Body — 2-col: left prose stack, right tests-included list */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2, mb: 2, width: '100%',
        }}
      >
        <Stack spacing={1.5}>
          <ProseCardShimmer lines={5} extraTags />
          <ProseCardShimmer lines={3} />
          {/* Mint-tinted "Preparation" card */}
          <Box sx={{ bgcolor: 'var(--mint)', border: '1.5px solid var(--sage-p)', borderRadius: '16px', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, px: 2.25, borderBottom: '1px solid var(--sage-p)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Shimmer width={16} height={16} circular />
              <Shimmer width={160} height={10} rounded={4} />
            </Box>
            <Box sx={{ p: 2.25 }}>
              <Shimmer width="100%" height={11} rounded={4} sx={{ mb: 1 }} />
              <Shimmer width="95%" height={11} rounded={4} sx={{ mb: 1 }} />
              <Shimmer width="60%" height={11} rounded={4} />
            </Box>
          </Box>
        </Stack>

        {/* Right: Tests Included */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
          <Box
            sx={{
              bgcolor: 'var(--forest)',
              p: '10px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Shimmer width={130} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
            <Shimmer width={90} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
          </Box>
          <Box sx={{ p: 2.5 }}>
            {Array.from({ length: 3 }).map((_, gi) => (
              <Box key={gi} sx={{ mb: 2.5 }}>
                <Shimmer width={110} height={10} rounded={4} sx={{ mb: 1.25 }} />
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {Array.from({ length: 5 + (gi % 2) }).map((_, ti) => (
                    <Shimmer
                      key={ti}
                      width={60 + ((ti * 13) % 50)}
                      height={22}
                      rounded={100}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* FAQ rows */}
      <Box sx={{ mb: 3 }}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: '#fff',
              border: '1px solid var(--border-l)',
              borderRadius: '12px',
              mb: 1, px: 2.25, py: 1.75,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Shimmer width={`${55 + i * 12}%`} height={12} rounded={4} />
            <Shimmer width={16} height={16} circular />
          </Box>
        ))}
      </Box>

      {/* Bottom CTA band */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          borderRadius: '18px',
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2.5, md: 2.75 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Shimmer width={130} height={16} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.14)', mb: 0.75 }} />
          <Shimmer width={260} height={11} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Shimmer width={90} height={24} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />
          <Shimmer width={130} height={40} rounded={100} sx={{ bgcolor: 'rgba(201,125,8,0.45)' }} />
          <Shimmer width={110} height={36} rounded={100} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default PackageDetailShimmer;
